import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, Target } from 'lucide-react';
import type { GoalStatus, GoalTargetType } from '@pms/shared';
import { goalsService } from '@/services/goals.service';
import { departmentsService } from '@/services/departments.service';
import { SearchInput } from '@/components/ui/search-input';
import { StatCard } from '@/components/ui/stat-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ALL_GOAL_STATUSES,
  ALL_GOAL_TARGET_TYPES,
  ALL_OPTION,
  GOAL_STATUS_META,
  GOAL_TARGET_TYPE_META,
} from '@/components/goals/goalConstants';
import { GoalsTable } from './GoalsTable';

interface Props {
  onOpen: (id: string) => void;
  /** Pins the view to one status (the Reviews tab pins it to `submitted`). */
  lockedStatus?: GoalStatus;
  /** Copy shown when nothing matches. */
  emptyMessage?: string;
  /** Reviews queue does not need the KPI strip. */
  showStats?: boolean;
}

const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE = 20;

export function TeamGoalsTab({
  onOpen,
  lockedStatus,
  emptyMessage = 'No goals match the current filters',
  showStats = true,
}: Props) {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [status, setStatus] = useState('');
  const [targetType, setTargetType] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [page, setPage] = useState(1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedQ(q); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q]);

  const { data: categories = [] } = useQuery({
    queryKey: ['goals', 'categories'],
    queryFn: () => goalsService.listCategories(),
  });

  const { data: deptData } = useQuery({
    queryKey: ['departments', 'picker'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  const summaryQuery = useQuery({
    queryKey: ['goals', 'summary', 'team'],
    queryFn: () => goalsService.summary(false),
    enabled: showStats,
  });

  const params = {
    page,
    limit: PAGE_SIZE,
    q: debouncedQ || undefined,
    status: (lockedStatus ?? (status || undefined)) as GoalStatus | undefined,
    target_type: (targetType || undefined) as GoalTargetType | undefined,
    category_id: categoryId || undefined,
    department_id: departmentId || undefined,
    sort: 'due_date:asc',
  };

  const listQuery = useQuery({
    queryKey: ['goals', 'list', params],
    queryFn: () => goalsService.listGoals(params),
  });

  const summary = summaryQuery.data;
  const pagination = listQuery.data?.pagination;

  /** Every filter change resets to page 1 — otherwise page 3 of a narrower list is empty. */
  const updateFilter = (setter: (v: string) => void) => (v: string) => {
    setter(v === ALL_OPTION ? '' : v);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {showStats && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Active" value={summary?.by_status.active ?? '—'} icon={Target} tone="teal" hint="In progress across your scope" />
          <StatCard label="Awaiting Review" value={summary?.awaiting_review ?? '—'} icon={CheckCircle2} tone="violet" hint="Submitted and waiting on you" />
          <StatCard label="At Risk" value={summary?.at_risk ?? '—'} icon={Clock} tone="amber" hint="Behind the expected pace" />
          <StatCard label="Overdue" value={summary?.overdue ?? '—'} icon={AlertTriangle} tone="rose" hint="Past the deadline" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search goals or employees…"
          containerClassName="min-w-[200px] flex-1 max-w-xs"
        />

        {!lockedStatus && (
          <Select value={status || ALL_OPTION} onValueChange={updateFilter(setStatus)}>
            <SelectTrigger className="h-10 w-40">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_OPTION}>All statuses</SelectItem>
              {ALL_GOAL_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{GOAL_STATUS_META[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={targetType || ALL_OPTION} onValueChange={updateFilter(setTargetType)}>
          <SelectTrigger className="h-10 w-36">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_OPTION}>All types</SelectItem>
            {ALL_GOAL_TARGET_TYPES.map((t) => (
              <SelectItem key={t} value={t}>{GOAL_TARGET_TYPE_META[t].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={categoryId || ALL_OPTION} onValueChange={updateFilter(setCategoryId)}>
          <SelectTrigger className="h-10 w-44">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_OPTION}>All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={departmentId || ALL_OPTION} onValueChange={updateFilter(setDepartmentId)}>
          <SelectTrigger className="h-10 w-44">
            <SelectValue placeholder="All departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_OPTION}>All departments</SelectItem>
            {(deptData?.data ?? []).map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {pagination && (
          <span className="ml-auto text-xs text-muted-foreground">
            {pagination.total} goal{pagination.total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <GoalsTable
        rows={listQuery.data?.data ?? []}
        loading={listQuery.isLoading}
        error={listQuery.isError}
        onRetry={() => void queryClient.invalidateQueries({ queryKey: ['goals', 'list'] })}
        onOpen={onOpen}
        empty={emptyMessage}
        pagination={
          pagination
            ? {
                page: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                onPageChange: setPage,
              }
            : undefined
        }
      />
    </div>
  );
}
